import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAnnounce } from './LiveRegion';

/**
 * A message with a tone and a heading — design-system plan DS-6a (5.1; F382, F36, F91, F120, F244, F363, F62, F150,
 * F179, F503).
 *
 * 🔴 A BANNER THAT REPORTS AN EVENT IS HEARD, NOT ONLY SEEN — `announce`. A banner that appears because something just
 * happened says so to a screen reader: `tone="danger"` becomes `role="alert"` and interrupts (the person pressed
 * something and it did not happen), and any other tone is said once in the page's polite live region when it appears
 * or its title changes (`useAnnounce`) — "Saved.", "Could not reach the server.". A banner that describes the record
 * or the screen — "This invoice was voided.", "This need not tie exactly to the return's Part 3." — is not announced:
 * it was there before the person arrived and is read with the page. The tone does not decide (DS-6a review: tying the
 * alert to `danger` interrupted on every visit to a voided invoice); the call site does, by passing `announce`.
 *
 * `action` is one control beside the message (the stale-version banner's "Reload their version"), `detail` a muted
 * line under it (a reference, a time), `onDismiss` adds a named close button. `title` is optional: a banner may be
 * its sentence alone.
 *
 * 🔴 THE BODY IS A `div`, NOT A `p` — ERP Phase 9 increment 13: a paragraph may not contain a paragraph, and the
 * parser closes the outer one at the first `<p>` it meets.
 */
export type BannerTone = 'info' | 'warning' | 'danger' | 'success';

export function Banner({
  tone,
  title,
  children,
  action,
  detail,
  onDismiss,
  announce = false,
}: {
  tone: BannerTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  detail?: ReactNode;
  onDismiss?: () => void;
  /** This banner reports something that just happened: an alert when `danger`, said politely otherwise. */
  announce?: boolean;
}) {
  const say = useAnnounce();
  const polite = announce && tone !== 'danger';
  // The title and, when it is plain text, the body: "Done" alone told nobody which journal the repair went to
  // (DS-6a codemod 3 review). A title without its own full stop gets one, so the two are heard as two sentences.
  const body = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
  const heading = title && body && !/[.!?…:]$/.test(title) ? `${title}.` : title;
  const sentence = [heading, body].filter(Boolean).join(' ');
  useEffect(() => {
    if (polite && sentence) return say(sentence);
    return undefined;
  }, [polite, sentence, say]);
  return (
    <div className={`wb-banner wb-banner--${tone}`} role={announce && tone === 'danger' ? 'alert' : undefined}>
      <div className="wb-banner__main">
        {title ? <span className="wb-banner__title">{title}</span> : null}
        {children ? <div className="wb-banner__body">{children}</div> : null}
        {detail ? <div className="wb-banner__detail">{detail}</div> : null}
      </div>
      {action ? <div className="wb-banner__aside">{action}</div> : null}
      {onDismiss ? (
        <button type="button" className="wb-banner__dismiss" aria-label={title ? `Dismiss: ${title}` : 'Dismiss'} onClick={onDismiss}>
          ×
        </button>
      ) : null}
    </div>
  );
}
